import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveDynamicComponent as _resolveDynamicComponent, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, normalizeStyle as _normalizeStyle, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "fixed inset-0 z-50 flex justify-end bg-slate-900/40 backdrop-blur-sm transition duration-300"
}
const _hoisted_2 = { class: "relative w-full max-w-lg bg-white h-screen shadow-2xl flex flex-col z-10 transition-transform duration-300 animate-slide-in" }
const _hoisted_3 = { class: "flex items-center gap-3.5" }
const _hoisted_4 = { class: "min-w-0" }
const _hoisted_5 = { class: "flex items-center flex-wrap gap-2 mt-1" }
const _hoisted_6 = { class: "grid grid-cols-4 gap-3" }
const _hoisted_7 = { class: "bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-center" }
const _hoisted_8 = { class: "bg-blue-50 border border-blue-100 rounded-xl p-3 text-center" }
const _hoisted_9 = { class: "bg-amber-50 border border-amber-100 rounded-xl p-3 text-center" }
const _hoisted_10 = { class: "bg-slate-50 border border-slate-100 rounded-xl p-3 text-center" }
const _hoisted_11 = { class: "space-y-2 text-left" }
const _hoisted_12 = { class: "flex border-b border-slate-100" }
const _hoisted_13 = ["onClick"]
const _hoisted_14 = { class: "space-y-4" }
const _hoisted_15 = {
  key: 0,
  class: "text-center p-8 border border-dashed border-slate-200 bg-slate-50/50 rounded-2xl"
}
const _hoisted_16 = { class: "flex items-start justify-between gap-3" }
const _hoisted_17 = { class: "space-y-1" }
const _hoisted_18 = { class: "space-y-2" }
const _hoisted_19 = { class: "flex justify-end gap-2 pt-2 border-t border-slate-100" }

import { ref, computed, watch, inject } from "vue";
import { useRouter } from "vue-router";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import DMSuiteIcon from "@/components/iconify/DMSuiteIcon.vue";
import { DASHBOARD_ICONS } from "@/config/dashboardIcons";


export default /*@__PURE__*/_defineComponent({
  __name: 'ServiceDetailsDrawer',
  props: {
    open: { type: Boolean },
    service: {}
  },
  emits: ["update:open"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const comfortMode = inject<any>("comfortMode", ref("comfortable"));

const router = useRouter();
const activeTab = ref("all");

const tabs = [
  { id: "all", label: "All Tasks" },
  { id: "in_progress", label: "On-going" },
  { id: "needs_review", label: "Needs Review" },
  { id: "completed", label: "Closed" }
];

// Clean tasks inside the service
const serviceTasks = computed(() => {
  if (!props.service || !Array.isArray(props.service.tasks)) {
    return [];
  }
  return props.service.tasks.map((task: any) => {
    return {
      id: task.id,
      task_id: task.task_id || task.id,
      title: task.title || task.name || task.subject || "Untitled Task",
      status: task.status || "Open",
      progress: task.progress || task.progress_percentage || 0,
      due_date: task.due_date || task.dueDate || null,
      assigned_to: task.assigned_to || task.assignedUser?.name || "Unassigned",
      description: task.instructions || task.description || ""
    };
  });
});

const taskCounts = computed(() => {
  const counts = { completed: 0, in_progress: 0, pending: 0, needs_review: 0 };
  serviceTasks.value.forEach((t) => {
    const s = t.status.toLowerCase();
    if (s.includes("completed") || s.includes("done") || s.includes("closed")) {
      counts.completed++;
    } else if (s.includes("progress") || s.includes("ongoing")) {
      counts.in_progress++;
    } else if (s.includes("review") || s.includes("feedback") || s.includes("approval")) {
      counts.needs_review++;
    } else {
      counts.pending++;
    }
  });
  return counts;
});

const filteredTasks = computed(() => {
  const list = serviceTasks.value;
  if (activeTab.value === "all") return list;

  return list.filter((task) => {
    const s = task.status.toLowerCase();
    if (activeTab.value === "completed") {
      return s.includes("completed") || s.includes("done") || s.includes("closed");
    }
    if (activeTab.value === "in_progress") {
      return s.includes("progress") || s.includes("ongoing");
    }
    if (activeTab.value === "needs_review") {
      return s.includes("review") || s.includes("feedback") || s.includes("approval");
    }
    return false;
  });
});

function close() {
  emit("update:open", false);
}

function handleViewTask(task: any) {
  close();
  router.push({
    path: "/apps/task-dashboard",
    query: {
      taskId: String(task.task_id),
      focusTask: "true",
      source: "service-drawer"
    }
  });
}

function handleReviewTask(task: any) {
  close();
  router.push({
    path: "/apps/task-dashboard",
    query: {
      taskId: String(task.task_id),
      focusTask: "true",
      highlight: "true",
      source: "service-drawer"
    }
  });
}

function getTaskStatusClass(status: string) {
  const normalized = status.toLowerCase();
  if (normalized.includes("completed") || normalized.includes("done") || normalized.includes("closed")) {
    return "bg-emerald-50 text-emerald-700 border-emerald-100";
  }
  if (normalized.includes("progress") || normalized.includes("ongoing")) {
    return "bg-blue-50 text-blue-700 border-blue-100";
  }
  if (normalized.includes("review") || normalized.includes("feedback") || normalized.includes("approval")) {
    return "bg-amber-50 text-amber-700 border-amber-100";
  }
  return "bg-slate-50 text-slate-600 border-slate-100";
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return "No due date";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// Reset tab when active service changes
watch(() => props.service?.id, () => {
  activeTab.value = "all";
});

return (_ctx: any,_cache: any) => {
  return (__props.open)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", {
          class: "absolute inset-0",
          onClick: close
        }),
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", {
            class: _normalizeClass(["border-b border-slate-100 flex items-center justify-between text-left", _unref(comfortMode) === 'comfortable' ? 'p-6 md:p-8' : 'p-6'])
          }, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("div", {
                class: _normalizeClass(["flex items-center justify-center shrink-0", [
              __props.service?.iconWrapClass || 'bg-indigo-50 text-indigo-600',
              _unref(comfortMode) === 'comfortable' ? 'h-13 w-13 rounded-2xl' : 'h-11 w-11 rounded-xl'
            ]])
              }, [
                (typeof __props.service?.icon === 'string')
                  ? (_openBlock(), _createBlock(DMSuiteIcon, {
                      key: 0,
                      icon: __props.service.icon,
                      size: _unref(comfortMode) === 'comfortable' ? 26 : 22,
                      "custom-class": __props.service?.iconClass || 'text-indigo-600'
                    }, null, 8, ["icon", "size", "custom-class"]))
                  : (_openBlock(), _createBlock(_resolveDynamicComponent(__props.service?.icon), {
                      key: 1,
                      class: _normalizeClass([
                __props.service?.iconClass || 'text-indigo-600',
                _unref(comfortMode) === 'comfortable' ? 'size-6.5' : 'size-5'
              ])
                    }, null, 8, ["class"]))
              ], 2),
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("h2", {
                  class: _normalizeClass(
                _unref(cn)(
                  'font-black text-slate-900 truncate',
                  _unref(comfortMode) === 'comfortable' ? 'text-xl md:text-2xl' : 'text-lg font-extrabold text-slate-800'
                )
              )
                }, _toDisplayString(__props.service?.label || 'Service Details'), 3),
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("span", {
                    class: _normalizeClass(
                  _unref(cn)(
                    'font-semibold',
                    _unref(comfortMode) === 'comfortable' ? 'text-sm text-slate-500' : 'text-xs text-slate-400 font-semibold'
                  )
                )
                  }, _toDisplayString(__props.service?.description), 3),
                  _createVNode(_unref(Badge), {
                    variant: "outline",
                    class: _normalizeClass(["rounded-full font-bold whitespace-nowrap", [
                  __props.service?.badgeClass,
                  _unref(comfortMode) === 'comfortable' ? 'px-2.5 py-0.5 text-xs' : 'px-2 py-0.2 text-[10px]'
                ]])
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(__props.service?.status), 1)
                    ]),
                    _: 1
                  }, 8, ["class"])
                ])
              ])
            ]),
            _createElementVNode("button", {
              class: "h-8.5 w-8.5 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 transition",
              onClick: close
            }, [
              _createVNode(DMSuiteIcon, {
                icon: _unref(DASHBOARD_ICONS).close,
                size: 18
              }, null, 8, ["icon"])
            ])
          ], 2),
          _createElementVNode("div", {
            class: _normalizeClass(["flex-1 overflow-y-auto", _unref(comfortMode) === 'comfortable' ? 'p-6 md:p-8 space-y-7' : 'p-6 space-y-6'])
          }, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("p", {
                  class: _normalizeClass(
                _unref(cn)(
                  'font-black text-emerald-700',
                  _unref(comfortMode) === 'comfortable' ? 'text-xl md:text-2xl' : 'text-lg'
                )
              )
                }, _toDisplayString(taskCounts.value.completed), 3),
                _createElementVNode("p", {
                  class: _normalizeClass(
                _unref(cn)(
                  'font-bold uppercase',
                  _unref(comfortMode) === 'comfortable' ? 'text-[11px] text-emerald-600' : 'text-[10px] text-emerald-500'
                )
              )
                }, " Closed ", 2)
              ]),
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("p", {
                  class: _normalizeClass(
                _unref(cn)(
                  'font-black text-blue-700',
                  _unref(comfortMode) === 'comfortable' ? 'text-xl md:text-2xl' : 'text-lg'
                )
              )
                }, _toDisplayString(taskCounts.value.in_progress), 3),
                _createElementVNode("p", {
                  class: _normalizeClass(
                _unref(cn)(
                  'font-bold uppercase',
                  _unref(comfortMode) === 'comfortable' ? 'text-[11px] text-blue-600' : 'text-[10px] text-blue-500'
                )
              )
                }, " Active ", 2)
              ]),
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("p", {
                  class: _normalizeClass(
                _unref(cn)(
                  'font-black text-amber-700',
                  _unref(comfortMode) === 'comfortable' ? 'text-xl md:text-2xl' : 'text-lg'
                )
              )
                }, _toDisplayString(taskCounts.value.needs_review), 3),
                _createElementVNode("p", {
                  class: _normalizeClass(
                _unref(cn)(
                  'font-bold uppercase',
                  _unref(comfortMode) === 'comfortable' ? 'text-[11px] text-amber-600' : 'text-[10px] text-amber-500'
                )
              )
                }, " Review ", 2)
              ]),
              _createElementVNode("div", _hoisted_10, [
                _createElementVNode("p", {
                  class: _normalizeClass(
                _unref(cn)(
                  'font-black text-slate-700',
                  _unref(comfortMode) === 'comfortable' ? 'text-xl md:text-2xl' : 'text-lg'
                )
              )
                }, _toDisplayString(taskCounts.value.pending), 3),
                _createElementVNode("p", {
                  class: _normalizeClass(
                _unref(cn)(
                  'font-bold uppercase',
                  _unref(comfortMode) === 'comfortable' ? 'text-[11px] text-slate-600' : 'text-[10px] text-slate-500'
                )
              )
                }, " Open Ticket ", 2)
              ])
            ]),
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("div", {
                class: _normalizeClass(
              _unref(cn)(
                'flex items-center justify-between font-bold text-slate-700',
                _unref(comfortMode) === 'comfortable' ? 'text-base text-slate-800' : 'text-sm'
              )
            )
              }, [
                _cache[1] || (_cache[1] = _createElementVNode("span", null, "Overall Progress", -1)),
                _createElementVNode("span", null, _toDisplayString(__props.service?.progress) + "%", 1)
              ], 2),
              _createElementVNode("div", {
                class: _normalizeClass(["w-full bg-slate-100 rounded-full overflow-hidden", _unref(comfortMode) === 'comfortable' ? 'h-3.5' : 'h-2.5'])
              }, [
                _createElementVNode("div", {
                  class: _normalizeClass(["h-full rounded-full transition-all duration-500", __props.service?.progressClass || 'bg-indigo-600']),
                  style: _normalizeStyle({ width: `${__props.service?.progress}%` })
                }, null, 6)
              ], 2)
            ]),
            _createElementVNode("div", _hoisted_12, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(tabs, (t) => {
                return _createElementVNode("button", {
                  key: t.id,
                  class: _normalizeClass(["flex-1 text-center transition duration-200", [
              activeTab.value === t.id 
                ? 'border-indigo-600 text-indigo-600 border-b-2 font-black' 
                : 'border-transparent text-slate-500 hover:text-slate-700 font-bold',
              _unref(comfortMode) !== 'comfortable' ? 'pb-3.5 text-base md:text-lg' : 'pb-3 text-sm'
            ]]),
                  onClick: ($event: any) => (activeTab.value = t.id)
                }, _toDisplayString(t.label), 11, _hoisted_13)
              }), 64))
            ]),
            _createElementVNode("div", _hoisted_14, [
              (filteredTasks.value.length === 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_15, [...(_cache[2] || (_cache[2] = [
                    _createElementVNode("p", { class: "text-sm text-slate-400 font-semibold" }, " No tasks found for this status. ", -1)
                  ]))]))
                : _createCommentVNode("", true),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredTasks.value, (task) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: task.id,
                  class: _normalizeClass(["bg-white border border-slate-200/80 rounded-2xl shadow-sm hover:shadow-md transition duration-200 text-left", [
              _unref(comfortMode) === 'comfortable' ? 'p-5 md:p-6 space-y-4' : 'p-4 space-y-3'
            ]])
                }, [
                  _createElementVNode("div", _hoisted_16, [
                    _createElementVNode("div", _hoisted_17, [
                      _createElementVNode("h4", {
                        class: _normalizeClass(
                    _unref(cn)(
                      'font-black text-slate-900 leading-snug',
                      _unref(comfortMode) === 'comfortable' ? 'text-base md:text-lg' : 'text-sm sm:text-base font-extrabold'
                    )
                  )
                      }, _toDisplayString(task.title), 3),
                      _createElementVNode("p", {
                        class: _normalizeClass(
                    _unref(cn)(
                      'font-semibold',
                      _unref(comfortMode) === 'comfortable' ? 'text-sm text-slate-500' : 'text-xs text-slate-400'
                    )
                  )
                      }, " Due: " + _toDisplayString(formatDate(task.due_date)) + " • Assignee: " + _toDisplayString(task.assigned_to), 3)
                    ]),
                    _createVNode(_unref(Badge), {
                      variant: "outline",
                      class: _normalizeClass(["rounded-full font-bold whitespace-nowrap", [
                  getTaskStatusClass(task.status),
                  _unref(comfortMode) === 'comfortable' ? 'px-2.5 py-0.5 text-xs' : 'px-2 py-0.2 text-[10px]'
                ]])
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(task.status), 1)
                      ]),
                      _: 2
                    }, 1032, ["class"])
                  ]),
                  (task.description)
                    ? (_openBlock(), _createElementBlock("p", {
                        key: 0,
                        class: _normalizeClass(
                _unref(cn)(
                  'leading-relaxed font-medium max-h-20 overflow-y-auto',
                  _unref(comfortMode) === 'comfortable' ? 'text-sm text-slate-600' : 'text-xs text-slate-500 font-medium'
                )
              )
                      }, _toDisplayString(task.description), 3))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_18, [
                    _createElementVNode("div", {
                      class: _normalizeClass(
                  _unref(cn)(
                    'flex items-center justify-between font-bold uppercase tracking-wider',
                    _unref(comfortMode) === 'comfortable' ? 'text-xs text-slate-500' : 'text-[10px] text-slate-400'
                  )
                )
                    }, [
                      _cache[3] || (_cache[3] = _createElementVNode("span", null, "Task Completion", -1)),
                      _createElementVNode("span", null, _toDisplayString(task.progress) + "%", 1)
                    ], 2),
                    _createElementVNode("div", {
                      class: _normalizeClass(["w-full bg-slate-100 rounded-full overflow-hidden", _unref(comfortMode) === 'comfortable' ? 'h-2.5' : 'h-1.5'])
                    }, [
                      _createElementVNode("div", {
                        class: "h-full bg-indigo-600 rounded-full transition-all duration-300",
                        style: _normalizeStyle({ width: `${task.progress}%` })
                      }, null, 4)
                    ], 2)
                  ]),
                  _createElementVNode("div", _hoisted_19, [
                    _createVNode(_unref(Button), {
                      size: "sm",
                      variant: "ghost",
                      class: _normalizeClass(
                  _unref(cn)(
                    'font-bold text-slate-600',
                    _unref(comfortMode) === 'comfortable' ? 'h-10 px-4 text-sm' : 'h-8 text-xs font-bold'
                  )
                ),
                      onClick: ($event: any) => (handleViewTask(task))
                    }, {
                      default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                        _createTextVNode(" View Task ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["class", "onClick"]),
                    _createVNode(_unref(Button), {
                      size: "sm",
                      class: _normalizeClass(
                  _unref(cn)(
                    'bg-indigo-600 text-white font-bold',
                    _unref(comfortMode) === 'comfortable' ? 'h-10 px-5 text-sm rounded-xl' : 'h-8 px-4 text-xs'
                  )
                ),
                      onClick: ($event: any) => (handleReviewTask(task))
                    }, {
                      default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                        _createTextVNode(" Review ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["class", "onClick"])
                  ])
                ], 2))
              }), 128))
            ])
          ], 2),
          _createElementVNode("div", {
            class: _normalizeClass(["border-t border-slate-100 bg-slate-50/50 flex gap-4", _unref(comfortMode) === 'comfortable' ? 'p-6 md:p-8' : 'p-6'])
          }, [
            _createVNode(_unref(Button), {
              variant: "outline",
              class: _normalizeClass(
            _unref(cn)(
              'flex-1 font-bold border-slate-200 text-slate-600',
              _unref(comfortMode) === 'comfortable' ? 'min-h-12 text-sm md:text-base rounded-xl' : 'text-xs py-3 rounded-xl'
            )
          ),
              onClick: close
            }, {
              default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                _createTextVNode(" Close Details ", -1)
              ]))]),
              _: 1
            }, 8, ["class"]),
            _createVNode(_unref(Button), {
              class: _normalizeClass(
            _unref(cn)(
              'flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold flex items-center justify-center gap-1.5',
              _unref(comfortMode) === 'comfortable' ? 'min-h-12 text-sm md:text-base rounded-xl' : 'text-xs py-3 rounded-xl'
            )
          ),
              onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(router).push('/apps/task-dashboard')))
            }, {
              default: _withCtx(() => [
                _cache[7] || (_cache[7] = _createTextVNode(" Open Task Dashboard ", -1)),
                _createVNode(DMSuiteIcon, {
                  icon: _unref(DASHBOARD_ICONS).externalLink,
                  size: 16
                }, null, 8, ["icon"])
              ]),
              _: 1
            }, 8, ["class"])
          ], 2)
        ])
      ]))
    : _createCommentVNode("", true)
}
}

})